import { useCallback, useEffect, useState } from "react"
import {
  AlertCircle,
  FileText,
  Loader2,
  PackageCheck,
  RefreshCw,
  Truck,
  CalendarClock,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import AdHocCommands from "@/api/adHocCommands"
import NavigatorVehicleDetailsParser, {
  classifyTransports,
  type NavigatorTransportAttachment,
  type NavigatorVehicleDetails,
  type NavigatorVehiclePlaceDetails,
  type NavigatorVehicleTransportDetails,
} from "@/lib/navigatorVehicleDetails"
import NavigatorAttachmentParser from "@/lib/navigatorAttachment"
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card"
import { cn } from "@/lib/utils"
import { getIntlLocale } from "@/lib/i18n/format"

type NavigatorMapOrdersProps = {
  driverJid: string
}

const formatDateTime = (value?: string) => {
  if (!value) return undefined
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat(getIntlLocale("pl-PL"), {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date)
}

const formatMoney = (value?: number, currency?: string) => {
  if (!value) return undefined
  if (!currency) return value.toLocaleString(getIntlLocale("pl-PL"))
  return new Intl.NumberFormat(getIntlLocale("pl-PL"), {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value)
}

const formatKm = (value?: number) => {
  if (value === undefined || value === null) return undefined
  return `${Math.round(value).toLocaleString(getIntlLocale("pl-PL"))} km`
}

const formatFreight = (transport: NavigatorVehicleTransportDetails) => {
  const preferEur = transport.unitPriceCurrency?.toUpperCase() === "EUR"
  if (preferEur && transport.freightEurNet) {
    return formatMoney(transport.freightEurNet, "EUR")
  }
  if (transport.freightPlnNet) {
    return formatMoney(transport.freightPlnNet, "PLN")
  }
  if (transport.freightEurNet) {
    return formatMoney(transport.freightEurNet, "EUR")
  }
  return undefined
}

const formatRatePerTon = (transport: NavigatorVehicleTransportDetails) => {
  if (
    transport.unitAmountType?.toLowerCase() === "ton" &&
    transport.unitPrice
  ) {
    return formatMoney(transport.unitPrice, transport.unitPriceCurrency)
  }
  const tons = transport.weightKg ? transport.weightKg / 1000 : undefined
  if (tons && transport.freightEurNet) {
    return formatMoney(transport.freightEurNet / tons, "EUR")
  }
  return undefined
}

const placeSummary = (place?: NavigatorVehiclePlaceDetails) => {
  if (!place) return undefined
  return [formatDateTime(place.realTime || place.scheduledTime), place.title]
    .filter(Boolean)
    .join(" · ")
}

const placesOfKind = (
  transport: NavigatorVehicleTransportDetails,
  kind: "pickup" | "delivery",
) => transport.places.filter((place) => place.kind === kind)

const normalizeText = (value?: string) =>
  value?.replace(/\r\n/g, "\n").trim() || undefined

const notesFor = (transport: NavigatorVehicleTransportDetails) => {
  const placeNotes = transport.places.flatMap((place) => [
    place.description,
    place.comments,
  ])
  const notes = [
    transport.commentsForCarrier,
    transport.description,
    transport.shipmentNotes,
    transport.requirements,
    ...placeNotes,
  ]
    .map(normalizeText)
    .filter(Boolean) as string[]
  return [...new Set(notes)].join("\n") || undefined
}

const InfoRow = ({
  label,
  value,
}: {
  label: string
  value?: string | number | null
}) => {
  if (value === undefined || value === null || value === "") return null
  return (
    <div className="flex min-w-0 justify-between gap-3 text-xs">
      <span className="shrink-0 text-gray-500 dark:text-gray-400">{label}</span>
      <span className="min-w-0 whitespace-pre-wrap break-words text-right font-medium text-gray-800 dark:text-gray-100">
        {value}
      </span>
    </div>
  )
}

const LongTextRow = ({ label, value }: { label: string; value?: string }) => {
  if (!value) return null
  if (value.length <= 40 && !value.includes("\n")) {
    return <InfoRow label={label} value={value} />
  }
  return (
    <div className="text-xs">
      <span className="text-gray-500 dark:text-gray-400">{label}</span>
      <div className="mt-0.5 max-h-20 overflow-auto whitespace-pre-wrap break-words rounded bg-gray-50 px-1.5 py-1 text-gray-700 dark:bg-gray-900 dark:text-gray-200">
        {value}
      </div>
    </div>
  )
}

type SlotKind = "previous" | "current" | "next"

const slotConfig: Record<
  SlotKind,
  {
    labelKey: `orders.navigatorMapOrders.slots.${SlotKind}`
    Icon: typeof Truck
    chipClass: string
  }
> = {
  previous: {
    labelKey: "orders.navigatorMapOrders.slots.previous",
    Icon: PackageCheck,
    chipClass:
      "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
  },
  current: {
    labelKey: "orders.navigatorMapOrders.slots.current",
    Icon: Truck,
    chipClass:
      "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300",
  },
  next: {
    labelKey: "orders.navigatorMapOrders.slots.next",
    Icon: CalendarClock,
    chipClass:
      "border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-300",
  },
}

const transportAttachments = (
  transport: NavigatorVehicleTransportDetails,
): NavigatorTransportAttachment[] => {
  if (transport.attachments?.length) return transport.attachments
  // Fallback for a backend that predates the full attachments list.
  return transport.orderAttachment ? [transport.orderAttachment] : []
}

const AttachmentRow = ({
  attachment,
}: {
  attachment: NavigatorTransportAttachment
}) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)

  const openAttachment = async () => {
    if (isLoading) return
    setIsLoading(true)
    // Open the window synchronously in the click handler — after the async
    // fetch the user gesture is gone and popup blockers would eat it.
    const attachmentWindow = window.open("", "_blank")
    try {
      const result = await AdHocCommands.getNavigatorAttachment(
        window.XMPP!,
        attachment.id,
        attachment.fileName,
      )
      const link = NavigatorAttachmentParser.parse(result)
      if (!link?.url) throw new Error("No attachment URL returned.")
      if (attachmentWindow) {
        attachmentWindow.location.href = link.url
      } else {
        window.open(link.url, "_blank", "noopener")
      }
    } catch (error) {
      attachmentWindow?.close()
      console.error("Failed to open Navigator attachment:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const addedAt = attachment.addedAt
    ? new Intl.DateTimeFormat(getIntlLocale("pl-PL"), {
        day: "2-digit",
        month: "2-digit",
      }).format(new Date(attachment.addedAt))
    : undefined

  return (
    <button
      type="button"
      onClick={openAttachment}
      title={attachment.fileName || t("orders.navigatorMapOrders.document")}
      className="flex w-full min-w-0 items-center gap-1.5 rounded px-1 py-0.5 text-left text-xs text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-950"
    >
      {isLoading ? (
        <Loader2 size={12} className="shrink-0 animate-spin" />
      ) : (
        <FileText size={12} className="shrink-0" />
      )}
      <span className="min-w-0 flex-1 truncate">
        {attachment.fileName ||
          t("orders.navigatorMapOrders.documentWithId", { id: attachment.id })}
      </span>
      {addedAt && (
        <span className="shrink-0 text-[10px] tabular-nums text-gray-400 dark:text-gray-500">
          {addedAt}
        </span>
      )}
    </button>
  )
}

const TransportPopoverContent = ({
  transport,
}: {
  transport: NavigatorVehicleTransportDetails
}) => {
  const { t } = useTranslation()
  const pickups = placesOfKind(transport, "pickup")
  const deliveries = placesOfKind(transport, "delivery")
  const pickupRefs = pickups
    .map((place) => normalizeText(place.loadingNumber))
    .filter(Boolean)
    .join("\n")
  const deliveryRefs = deliveries
    .map((place) => normalizeText(place.loadingNumber))
    .filter(Boolean)
    .join("\n")
  const freight = formatFreight(transport)
  const ratePerTon = formatRatePerTon(transport)
  const cargo = [transport.cargo, transport.cargoAmount]
    .filter(Boolean)
    .join(", ")
  const kilometers = [
    formatKm(transport.kmWithoutCargo) &&
      t("orders.navigatorMapOrders.emptyKm", {
        km: formatKm(transport.kmWithoutCargo),
      }),
    formatKm(transport.kmWithCargo) &&
      t("orders.navigatorMapOrders.loadedKm", {
        km: formatKm(transport.kmWithCargo),
      }),
  ]
    .filter(Boolean)
    .join(" · ")
  const notes = notesFor(transport)
  const attachments = transportAttachments(transport)

  return (
    <div className="space-y-1.5">
      <div className="flex min-w-0 items-center justify-between gap-2">
        <span className="truncate text-xs font-semibold text-gray-900 dark:text-white">
          {transport.number || transport.transportId}
        </span>
      </div>
      <div className="space-y-1 border-t border-gray-100 pt-1.5 dark:border-gray-800">
        <InfoRow
          label={t("orders.navigatorMapOrders.customer")}
          value={transport.customer}
        />
        <InfoRow
          label={t("orders.navigatorMapOrders.clientOrderNumber")}
          value={transport.clientOrderNumber}
        />
        <InfoRow
          label={t("orders.navigatorMapOrders.route")}
          value={transport.route}
        />
        <InfoRow label={t("orders.navigatorMapOrders.cargo")} value={cargo} />
        {pickups.map((place) => (
          <InfoRow
            key={place.placeId}
            label={t("orders.navigatorMapOrders.loading")}
            value={placeSummary(place)}
          />
        ))}
        <LongTextRow
          label={t("orders.navigatorMapOrders.loadingRefs")}
          value={pickupRefs}
        />
        {deliveries.map((place) => (
          <InfoRow
            key={place.placeId}
            label={t("orders.navigatorMapOrders.unloading")}
            value={placeSummary(place)}
          />
        ))}
        <LongTextRow
          label={t("orders.navigatorMapOrders.unloadingRefs")}
          value={deliveryRefs}
        />
        <InfoRow label="Km" value={kilometers} />
        <InfoRow
          label={t("orders.navigatorMapOrders.customerFreight")}
          value={
            freight && ratePerTon
              ? `${freight} (${ratePerTon}/t)`
              : freight || (ratePerTon ? `${ratePerTon}/t` : undefined)
          }
        />
      </div>
      {notes && (
        <div className="space-y-1 border-t border-gray-100 pt-1.5 dark:border-gray-800">
          <div className="text-xs font-medium text-gray-700 dark:text-gray-200">
            {t("orders.navigatorMapOrders.notes")}
          </div>
          <div className="max-h-24 overflow-auto whitespace-pre-wrap break-words rounded bg-gray-50 px-1.5 py-1 text-xs text-gray-600 dark:bg-gray-900 dark:text-gray-300">
            {notes}
          </div>
        </div>
      )}
      {attachments.length > 0 && (
        <div className="space-y-0.5 border-t border-gray-100 pt-1.5 dark:border-gray-800">
          <div className="text-xs font-medium text-gray-700 dark:text-gray-200">
            {t("orders.navigatorMapOrders.documents", {
              number: attachments.length,
            })}
          </div>
          <div className="max-h-28 overflow-auto">
            {attachments.map((attachment) => (
              <AttachmentRow key={attachment.id} attachment={attachment} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const SlotChip = ({
  kind,
  transport,
}: {
  kind: SlotKind
  transport?: NavigatorVehicleTransportDetails
}) => {
  const { t } = useTranslation()
  const { labelKey, Icon, chipClass } = slotConfig[kind]
  const label = t(labelKey)

  return (
    <HoverCard openDelay={100} closeDelay={200}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className={cn(
            "flex size-6 items-center justify-center rounded-full border shadow-sm transition-colors",
            transport
              ? chipClass
              : "border-gray-200 bg-white text-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-600",
          )}
        >
          <Icon size={13} />
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        align="center"
        side="bottom"
        className="w-72 max-w-[90vw] p-2"
      >
        <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
          {label}
        </div>
        {transport ? (
          <TransportPopoverContent transport={transport} />
        ) : (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {t("orders.navigatorMapOrders.noOrder")}
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  )
}

const NavigatorMapOrders = ({ driverJid }: NavigatorMapOrdersProps) => {
  const { t } = useTranslation()
  const [details, setDetails] = useState<NavigatorVehicleDetails | undefined>()
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)

  const loadDetails = useCallback(async () => {
    setIsLoading(true)
    setHasError(false)
    try {
      const result = await AdHocCommands.getNavigatorVehicleDetails(
        window.XMPP!,
        driverJid,
      )
      const parsed = NavigatorVehicleDetailsParser.parse(result)
      if (!parsed) throw new Error("Navigator response could not be parsed.")
      setDetails(parsed)
    } catch (error) {
      console.error("Failed to load Navigator vehicle details:", error)
      setHasError(true)
    } finally {
      setIsLoading(false)
    }
  }, [driverJid])

  useEffect(() => {
    setDetails(undefined)
    void loadDetails()
  }, [loadDetails])

  const slots = classifyTransports(details?.transports ?? [])

  return (
    <div
      className="absolute left-1/2 top-2 z-10 -translate-x-1/2"
      data-testid="navigator-map-orders"
    >
      <div className="flex items-center gap-1 rounded-full border border-gray-200 bg-white/95 p-0.5 shadow-md backdrop-blur dark:border-gray-700 dark:bg-gray-800/95">
        <SlotChip kind="previous" transport={slots.previous} />
        <SlotChip kind="current" transport={slots.current} />
        <SlotChip kind="next" transport={slots.next} />
        <button
          type="button"
          onClick={() => void loadDetails()}
          disabled={isLoading}
          title={
            hasError
              ? t("orders.navigatorMapOrders.loadError")
              : t("orders.navigatorMapOrders.refresh")
          }
          aria-label={t("orders.navigatorMapOrders.refresh")}
          className="flex size-5 items-center justify-center rounded-full text-gray-400 hover:text-gray-600 disabled:opacity-50 dark:text-gray-500 dark:hover:text-gray-300"
        >
          {hasError && !isLoading ? (
            <AlertCircle size={11} className="text-red-500" />
          ) : (
            <RefreshCw size={11} className={cn(isLoading && "animate-spin")} />
          )}
        </button>
      </div>
    </div>
  )
}

export default NavigatorMapOrders
